import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import * as pdfjsLib from 'pdfjs-dist';
import {
  CheckCircle,
  XCircle,
  ChevronLeft,
  ChevronRight,
  PenTool,
  AlertTriangle,
  Loader
} from 'lucide-react';
import { signingApi } from '../services/api';
import './SigningPage.css';

pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://unpkg.com/pdfjs-dist@4.10.38/build/pdf.worker.min.mjs';

const DEFAULT_PAPER_SIZE = { width: 612, height: 792 };

// Cursive/script fonts for stylized signatures
const SIGNATURE_FONTS = [
  { name: 'Dancing Script', css: "'Dancing Script', cursive" },
  { name: 'Great Vibes', css: "'Great Vibes', cursive" },
  { name: 'Pacifico', css: "'Pacifico', cursive" },
  { name: 'Sacramento', css: "'Sacramento', cursive" },
  { name: 'Caveat', css: "'Caveat', cursive" },
];

function SigningPage() {
  const { token } = useParams();

  // Document state
  const [document, setDocument] = useState(null);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // PDF rendering
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pdfPages, setPdfPages] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [canvasSize, setCanvasSize] = useState(DEFAULT_PAPER_SIZE);

  // Signing state
  const [activeFieldId, setActiveFieldId] = useState(null);
  const [signatureText, setSignatureText] = useState('');
  const [selectedFont, setSelectedFont] = useState(0);
  const [signing, setSigning] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [declined, setDeclined] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pendingLastField, setPendingLastField] = useState(null);

  const canvasAreaRef = useRef(null);
  const fieldRefs = useRef({});

  // Load document
  useEffect(() => {
    loadDocument();
  }, [token]);

  const loadDocument = async () => {
    try {
      setLoading(true);
      const res = await signingApi.getDocument(token);
      const doc = res.data;
      setDocument(doc);
      setFields(doc.fields || []);
      setSignatureText(doc.customer_name || '');

      if (doc.status === 'completed') {
        setCompleted(true);
      }

      // Load the file
      await loadFile(doc);

      // Auto-focus first unsigned required field
      const firstUnsigned = (doc.fields || []).find(f => f.required && !f.signed_at);
      if (firstUnsigned) {
        setTimeout(() => scrollToField(firstUnsigned), 500);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load document');
    } finally {
      setLoading(false);
    }
  };

  const loadFile = async (doc) => {
    const fileUrl = signingApi.getFile(token);

    if (doc.file_type === 'pdf') {
      try {
        const loadingTask = pdfjsLib.getDocument(fileUrl);
        const pdf = await loadingTask.promise;
        setPdfDoc(pdf);
        setCurrentPage(1);

        // Get actual page dimensions from the first page
        const firstPage = await pdf.getPage(1);
        const viewport = firstPage.getViewport({ scale: 1.0 });
        setCanvasSize({ width: viewport.width, height: viewport.height });
      } catch (err) {
        console.error('Error loading PDF:', err);
      }
    } else {
      // For images, load and get natural dimensions
      const img = new Image();
      img.onload = () => {
        // Scale to a reasonable display width (max 800px wide)
        const maxW = 800;
        const scale = img.naturalWidth > maxW ? maxW / img.naturalWidth : 1;
        setCanvasSize({ width: img.naturalWidth * scale, height: img.naturalHeight * scale });
      };
      img.src = fileUrl;
      setBackgroundImage(fileUrl);
    }
  };

  // Render PDF page at native dimensions
  const renderPdfPage = useCallback(async (pageNum) => {
    if (!pdfDoc || pdfPages[pageNum]) return;

    try {
      const page = await pdfDoc.getPage(pageNum);
      const canvas = window.document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const pixelRatio = 2;
      const viewport = page.getViewport({ scale: 1.0 });

      canvas.width = viewport.width * pixelRatio;
      canvas.height = viewport.height * pixelRatio;

      const scaledViewport = page.getViewport({ scale: pixelRatio });

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      await page.render({ canvasContext: ctx, viewport: scaledViewport }).promise;
      const dataUrl = canvas.toDataURL('image/png', 1.0);
      setPdfPages(prev => ({ ...prev, [pageNum]: dataUrl }));
    } catch (err) {
      console.error('Error rendering page:', err);
    }
  }, [pdfDoc, pdfPages]);

  useEffect(() => {
    if (pdfDoc) renderPdfPage(currentPage);
  }, [pdfDoc, currentPage, renderPdfPage]);

  const currentBackground = pdfDoc ? pdfPages[currentPage] : backgroundImage;
  const totalPages = pdfDoc ? pdfDoc.numPages : 1;

  // Scroll to a field
  const scrollToField = (field) => {
    if (field.page_number !== currentPage) {
      setCurrentPage(field.page_number);
    }
    setActiveFieldId(field.id);

    setTimeout(() => {
      const el = fieldRefs.current[field.id];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  // Get value for a field
  const getFieldValue = (field) => {
    if (field.field_type === 'signature') {
      return JSON.stringify({ text: signatureText, font: SIGNATURE_FONTS[selectedFont].name });
    } else if (field.field_type === 'initials') {
      const initials = signatureText.split(' ').map(w => w[0]).filter(Boolean).join('').toUpperCase();
      return JSON.stringify({ text: initials, font: SIGNATURE_FONTS[selectedFont].name });
    } else if (field.field_type === 'date') {
      return new Date().toLocaleDateString('en-US');
    } else if (field.field_type === 'name') {
      return signatureText || document.customer_name;
    } else if (field.field_type === 'email') {
      return document.customer_email;
    }
    return signatureText;
  };

  // Check if this is the last unsigned required field
  const isLastRequiredField = (field) => {
    const unsignedRequired = fields.filter(f => f.required && !f.signed_at && f.id !== field.id);
    return unsignedRequired.length === 0;
  };

  // Sign a field
  const handleSignField = async (field) => {
    if (signing || field.signed_at) return;

    const value = getFieldValue(field);

    // If this is the last required field, show confirmation modal instead of submitting
    if (isLastRequiredField(field)) {
      setPendingLastField({ ...field, value });
      setShowConfirmModal(true);
      return;
    }

    try {
      setSigning(true);
      const res = await signingApi.signField(token, field.id, value);

      setFields(prev => prev.map(f =>
        f.id === field.id ? { ...f, value, signed_at: new Date().toISOString() } : f
      ));

      if (res.data.next_field) {
        setTimeout(() => scrollToField(res.data.next_field), 300);
      }
    } catch (err) {
      console.error('Error signing:', err);
      alert(err.response?.data?.error || 'Failed to sign');
    } finally {
      setSigning(false);
    }
  };

  // Confirm and submit the final signature
  const handleConfirmSubmit = async () => {
    if (!pendingLastField || submitting) return;

    try {
      setSubmitting(true);
      await signingApi.signField(token, pendingLastField.id, pendingLastField.value);

      setFields(prev => prev.map(f =>
        f.id === pendingLastField.id ? { ...f, value: pendingLastField.value, signed_at: new Date().toISOString() } : f
      ));

      setShowConfirmModal(false);
      setPendingLastField(null);
      setActiveFieldId(null);
      setCompleted(true);
    } catch (err) {
      console.error('Error submitting:', err);
      alert(err.response?.data?.error || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
    setPendingLastField(null);
  };

  // Decline
  const handleDecline = async () => {
    try {
      await signingApi.decline(token, declineReason);
      setDeclined(true);
      setShowDeclineModal(false);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to decline');
    }
  };

  // Calculate font size to fit text within field dimensions
  const calcFontSize = (text, fieldWidth, fieldHeight, isSignature = false) => {
    if (isSignature) {
      const padX = 4, padY = 2;
      const availW = fieldWidth - padX * 2;
      const availH = fieldHeight - padY * 2;
      const fitsWidth = availW / (text.length * 0.45);
      const fitsHeight = availH * 0.9;
      return Math.max(10, Math.min(fitsWidth, fitsHeight, 60));
    }
    const availW = fieldWidth - 16;
    const availH = fieldHeight - 6;
    const fitsWidth = availW / (text.length * 0.5);
    const fitsHeight = availH * 0.7;
    return Math.max(8, Math.min(fitsWidth, fitsHeight, 18));
  };

  // Render value for a signed field
  const renderFieldValue = (field) => {
    if (!field.value) return null;

    if (field.field_type === 'signature' || field.field_type === 'initials') {
      try {
        const parsed = JSON.parse(field.value);
        const font = SIGNATURE_FONTS.find(f => f.name === parsed.font) || SIGNATURE_FONTS[0];
        const fontSize = calcFontSize(parsed.text, field.width, field.height, field.field_type === 'signature');
        return (
          <span className="signed-value" style={{ fontFamily: font.css, fontSize: `${fontSize}px` }}>
            {parsed.text}
          </span>
        );
      } catch {
        return <span className="signed-value">{field.value}</span>;
      }
    }
    const fontSize = calcFontSize(field.value, field.width, field.height, false);
    return <span className="signed-value typed" style={{ fontSize: `${fontSize}px` }}>{field.value}</span>;
  };

  // ── Loading / Error / Completed / Declined states ──

  if (loading) {
    return (
      <div className="signing-page">
        <div className="signing-status-screen">
          <Loader className="spin" size={48} />
          <h2>Loading document...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="signing-page">
        <div className="signing-status-screen error">
          <XCircle size={48} />
          <h2>{error}</h2>
          <p>Please contact the sender if you believe this is an error.</p>
        </div>
      </div>
    );
  }

  if (declined) {
    return (
      <div className="signing-page">
        <div className="signing-status-screen declined">
          <XCircle size={48} />
          <h2>Document Declined</h2>
          <p>You have declined to sign this document. The sender has been notified.</p>
        </div>
      </div>
    );
  }

  if (completed) {
    const completedPageFields = fields.filter(f => f.page_number === currentPage);
    return (
      <div className="signing-page">
        <link
          href="https://fonts.googleapis.com/css2?family=Caveat:wght@400;700&family=Dancing+Script:wght@400;700&family=Great+Vibes&family=Pacifico&family=Sacramento&display=swap"
          rel="stylesheet"
        />
        <div className="signing-completed-bar">
          <div className="signing-completed-bar-left">
            <CheckCircle size={18} />
            <span>Signing complete &mdash; {document?.name}</span>
          </div>
          <div className="signing-completed-bar-right">
            <a href={`/api/signing/${token}/download`} className="signing-action-btn primary" download>
              Download PDF
            </a>
          </div>
        </div>
        <div className="signing-completed-body">
          {totalPages > 1 && (
            <div className="signing-page-nav">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                <ChevronLeft size={16} />
              </button>
              <span>Page {currentPage} of {totalPages}</span>
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                <ChevronRight size={16} />
              </button>
            </div>
          )}
          <div
            className="signing-canvas"
            style={{
              width: canvasSize.width,
              height: canvasSize.height,
              backgroundImage: currentBackground ? `url(${currentBackground})` : undefined,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          >
            {completedPageFields.map(field => (
              <div
                key={field.id}
                style={{
                  position: 'absolute',
                  left: field.x,
                  top: field.y,
                  width: field.width,
                  height: field.height,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {renderFieldValue(field)}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Count progress
  const requiredFields = fields.filter(f => f.required);
  const signedFields = requiredFields.filter(f => f.signed_at);
  const progress = requiredFields.length > 0
    ? Math.round((signedFields.length / requiredFields.length) * 100)
    : 0;

  const currentPageFields = fields.filter(f => f.page_number === currentPage);

  return (
    <div className="signing-page">
      {/* Google Fonts for signatures */}
      <link
        href="https://fonts.googleapis.com/css2?family=Caveat:wght@400;700&family=Dancing+Script:wght@400;700&family=Great+Vibes&family=Pacifico&family=Sacramento&display=swap"
        rel="stylesheet"
      />

      {/* Top bar */}
      <div className="signing-topbar">
        <div className="signing-topbar-left">
          <PenTool size={20} />
          <div className="signing-doc-info">
            <span className="signing-doc-name">{document?.name}</span>
            <span className="signing-doc-for">For: {document?.customer_name}</span>
          </div>
        </div>

        <div className="signing-topbar-center">
          <div className="signing-progress">
            <div className="signing-progress-bar">
              <div className="signing-progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <span className="signing-progress-text">
              {signedFields.length} of {requiredFields.length} fields signed
            </span>
          </div>
        </div>

        <div className="signing-topbar-right">
          <button className="signing-decline-btn" onClick={() => setShowDeclineModal(true)}>
            Decline
          </button>
        </div>
      </div>

      <div className="signing-body">
        {/* Left panel - signature setup */}
        <div className="signing-sidebar">
          <div className="signing-sidebar-section">
            <h3>Your Signature</h3>
            <input
              type="text"
              className="signing-name-input"
              value={signatureText}
              onChange={(e) => setSignatureText(e.target.value)}
              placeholder="Type your name"
            />
          </div>

          <div className="signing-sidebar-section">
            <h3>Style</h3>
            <div className="signing-font-list">
              {SIGNATURE_FONTS.map((font, idx) => (
                <button
                  key={font.name}
                  className={`signing-font-option ${selectedFont === idx ? 'selected' : ''}`}
                  onClick={() => setSelectedFont(idx)}
                  style={{ fontFamily: font.css }}
                >
                  {signatureText || 'Your Name'}
                </button>
              ))}
            </div>
          </div>

          {/* Field list */}
          <div className="signing-sidebar-section">
            <h3>Fields ({signedFields.length}/{requiredFields.length})</h3>
            <div className="signing-field-list">
              {fields.map(field => (
                <button
                  key={field.id}
                  className={`signing-field-item ${field.signed_at ? 'done' : ''} ${activeFieldId === field.id ? 'active' : ''}`}
                  onClick={() => scrollToField(field)}
                >
                  {field.signed_at ? <CheckCircle size={14} /> : <PenTool size={14} />}
                  <span>{field.label || field.field_type} (p.{field.page_number})</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Document canvas */}
        <div className="signing-canvas-area" ref={canvasAreaRef}>
          {totalPages > 1 && (
            <div className="signing-page-nav">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                <ChevronLeft size={16} />
              </button>
              <span>Page {currentPage} of {totalPages}</span>
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                <ChevronRight size={16} />
              </button>
            </div>
          )}

          <div
            className="signing-canvas"
            style={{
              width: canvasSize.width,
              height: canvasSize.height,
              backgroundImage: currentBackground ? `url(${currentBackground})` : undefined,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          >
            {currentPageFields.map(field => (
              <div
                key={field.id}
                ref={el => { fieldRefs.current[field.id] = el; }}
                className={`signing-field ${field.signed_at ? 'signed' : 'unsigned'} ${activeFieldId === field.id ? 'active' : ''} field-type-${field.field_type}`}
                style={{
                  left: field.x,
                  top: field.y,
                  width: field.width,
                  height: field.height,
                }}
                onClick={() => !field.signed_at && setActiveFieldId(field.id)}
              >
                {field.signed_at ? (
                  <div className="signing-field-signed">
                    {renderFieldValue(field)}
                  </div>
                ) : (
                  <div
                    className={`signing-field-placeholder ${activeFieldId === field.id ? 'active' : ''}`}
                    onClick={(e) => {
                      if (activeFieldId === field.id) {
                        e.stopPropagation();
                        handleSignField(field);
                      }
                    }}
                  >
                    {(() => {
                      const isActive = activeFieldId === field.id;
                      let previewText = '';
                      let isScript = false;
                      let fontStyle = {};

                      if (field.field_type === 'signature') {
                        previewText = signatureText || 'Your signature';
                        isScript = true;
                        fontStyle = { fontFamily: SIGNATURE_FONTS[selectedFont].css };
                      } else if (field.field_type === 'initials') {
                        previewText = signatureText.split(' ').map(w => w[0]).filter(Boolean).join('').toUpperCase() || 'XX';
                        isScript = true;
                        fontStyle = { fontFamily: SIGNATURE_FONTS[selectedFont].css };
                      } else if (field.field_type === 'date') {
                        previewText = new Date().toLocaleDateString('en-US');
                      } else if (field.field_type === 'name') {
                        previewText = signatureText || document?.customer_name || '';
                      } else if (field.field_type === 'email') {
                        previewText = document?.customer_email || '';
                      }

                      const fontSize = calcFontSize(previewText, field.width, field.height, field.field_type === 'signature');

                      return isActive ? (
                        <>
                          <span className="signing-preview" style={{ ...fontStyle, fontSize: `${fontSize}px` }}>
                            {previewText}
                          </span>
                          <span className="signing-tap-hint">
                            {signing ? 'Signing...' : 'Tap to sign'}
                          </span>
                        </>
                      ) : (
                        <div className="signing-field-prompt">
                          <PenTool size={14} />
                          <span>{field.label || field.field_type}</span>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Confirm submission modal */}
      {showConfirmModal && pendingLastField && (
        <div className="signing-modal-overlay">
          <div className="signing-modal confirm-modal" onClick={e => e.stopPropagation()}>
            <CheckCircle size={40} className="signing-modal-icon confirm" />
            <h2>Confirm Your Signature</h2>
            <p>
              You are about to complete signing "<strong>{document?.name}</strong>".
              By clicking Submit, you agree that your electronic signature is the legal equivalent of your manual signature.
            </p>

            <div className="confirm-signature-preview">
              <span className="confirm-label">Your signature</span>
              <div className="confirm-signature-display">
                {(() => {
                  try {
                    const parsed = JSON.parse(pendingLastField.value);
                    const font = SIGNATURE_FONTS.find(f => f.name === parsed.font) || SIGNATURE_FONTS[0];
                    return <span style={{ fontFamily: font.css, fontSize: '32px' }}>{parsed.text}</span>;
                  } catch {
                    return <span style={{ fontSize: '18px' }}>{pendingLastField.value}</span>;
                  }
                })()}
              </div>
              <span className="confirm-signer">{document?.customer_name} &middot; {document?.customer_email}</span>
            </div>

            <div className="signing-modal-actions">
              <button className="signing-modal-cancel" onClick={handleCancelConfirm} disabled={submitting}>
                Go Back
              </button>
              <button className="signing-modal-submit" onClick={handleConfirmSubmit} disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit Signature'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Decline modal */}
      {showDeclineModal && (
        <div className="signing-modal-overlay" onClick={() => setShowDeclineModal(false)}>
          <div className="signing-modal" onClick={e => e.stopPropagation()}>
            <AlertTriangle size={32} className="signing-modal-icon" />
            <h2>Decline to Sign</h2>
            <p>Are you sure you want to decline signing this document? The sender will be notified.</p>
            <textarea
              className="signing-decline-reason"
              placeholder="Reason (optional)"
              value={declineReason}
              onChange={e => setDeclineReason(e.target.value)}
            />
            <div className="signing-modal-actions">
              <button className="signing-modal-cancel" onClick={() => setShowDeclineModal(false)}>Cancel</button>
              <button className="signing-modal-decline" onClick={handleDecline}>Decline to Sign</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SigningPage;
